import { useState } from 'react';
import { Link } from 'react-router';
import {
  GuideLayout,
  GuideSection,
  GuideTip,
  GuideSteps,
  GuideStep,
  GuideGrid,
  GuideCard,
} from '@/components/guides/guide-layout';

const inlineLink =
  'text-brand-600 hover:text-brand-700 font-medium underline decoration-brand-300 underline-offset-2';

const letterTemplate = `[Your name]
[Your address]
[Your email and phone number]

[Date]

Claims / Insurance Department
[Council name]
[Council address]

Dear Sir or Madam,

RE: Claim for vehicle damage caused by a pothole — [road name, town, postcode]

On [date] at approximately [time], I was driving my [make, model and registration] along [road name] in [town] when my vehicle struck a pothole located [describe the location precisely, e.g. "in the nearside lane, approximately 20 metres past the junction with Mill Road"].

The pothole measured approximately [width] cm across and [depth] cm deep. As a result of the impact, my vehicle sustained the following damage: [list the damage, e.g. "a punctured nearside front tyre and a buckled alloy wheel"].

Under Section 41 of the Highways Act 1980, the relevant highway authority has a statutory duty to maintain a highway maintainable at public expense. I ask you to assess whether the reported defect amounted to a breach of that duty and caused the documented repair loss of £[amount].

In support of my claim, I enclose:

- Photographs of the pothole, showing its size, depth and location
- Photographs of the damage to my vehicle
- An itemised repair [invoice / quotation] from [garage name]
- [Details of prior reports of this defect, if you have found any]

[If you found a possible prior report, add only after verification: A [FixMyStreet / portal] entry dated [date] may concern the same defect. Please confirm from your own records whether the authority received and matched it to this specific defect, and what inspection or repair response followed.]

Please acknowledge receipt of this letter within 14 days and provide your substantive response within 28 days. If you intend to rely on the special defence under Section 58 of the Highways Act 1980, please provide copies of your inspection records for this road covering the 12 months before my incident, together with details of any reports of this defect received prior to it.

If I do not receive a satisfactory response, I am prepared to pursue this claim through the small claims court without further notice.

Yours faithfully,

[Your name]`;

function LetterTemplateBlock() {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(letterTemplate);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="bg-white rounded-2xl border border-surface-200 mb-8 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between px-6 py-4 bg-brand-950">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-brand-800 rounded-xl flex items-center justify-center text-brand-300">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h2 className="font-display text-xl font-bold text-white">The Template</h2>
        </div>
        <button
          onClick={handleCopy}
          className="inline-flex items-center gap-2 px-4 py-2 bg-accent-500 text-white text-sm font-semibold rounded-lg hover:bg-accent-600 transition-colors"
        >
          {copied ? (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Copied!
            </>
          ) : (
            <>
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy template
            </>
          )}
        </button>
      </div>
      <div className="p-6 sm:p-8 bg-surface-50">
        <pre className="whitespace-pre-wrap font-serif text-sm sm:text-[15px] leading-relaxed text-surface-800">
          {letterTemplate}
        </pre>
      </div>
    </div>
  );
}

export function PotholeClaimLetterTemplateGuide() {
  return (
    <GuideLayout
      slug="pothole-claim-letter-template"
      category="Evidence"
      categoryColor="accent"
      title="Pothole Claim Letter Template (Free UK Template)"
      description="A free, ready-to-use letter template for claiming pothole damage compensation from your council — plus exactly what to include and the mistakes that get claims rejected."
      readTime="6 min"
      updatedDate="2026-07-09"
      datePublished="2026-07-09"
      reviewedFor="UK law"
      relatedGuides={[
        { slug: 'what-makes-a-valid-claim', title: 'What Makes a Valid Pothole Claim?', readTime: '5 min' },
        { slug: 'section-41-explained', title: 'Section 41 of the Highways Act', readTime: '6 min' },
        { slug: 'understanding-rejection-reasons', title: 'Common Rejection Reasons (And How to Respond)', readTime: '6 min' },
      ]}
      atAGlance={[
        { label: 'Legal basis', value: 'Section 41 of the Highways Act 1980 — the council\u2019s statutory duty to maintain the road' },
        { label: 'Send it to', value: 'The claims or insurance department of the highway authority responsible for the road' },
        { label: 'Must include', value: 'Date, time and precise location of the incident, pothole measurements, damage details and the amount claimed' },
        { label: 'Attach', value: 'Photos of the pothole and damage, an itemised repair quote or invoice, and any prior reports' },
        { label: 'Response time', value: 'Ask for acknowledgement within 14 days and a substantive response within 28 days' },
      ]}
      howTo={{
        name: 'How to Write a Pothole Claim Letter',
        description:
          'Five steps to write a factual claim letter that identifies the legal basis, evidence and records requested from the relevant authority.',
        steps: [
          {
            name: 'Identify the right highway authority',
            text: 'Confirm which council or authority maintains the road. Local roads belong to the local highway authority; motorways and major A-roads belong to National Highways.',
          },
          {
            name: 'State the facts precisely',
            text: 'Give the date, time, road name and exact location of the pothole, your vehicle details, and the pothole\u2019s approximate width and depth.',
          },
          {
            name: 'Cite Section 41 of the Highways Act 1980',
            text: 'State the Section 41 maintenance duty and ask the authority to assess the reported defect, causation and loss. Do not present liability as already established.',
          },
          {
            name: 'Attach your evidence',
            text: 'Enclose photographs of the pothole and the damage, an itemised repair quote or invoice, and details of any prior reports of the defect.',
          },
          {
            name: 'Request their Section 58 records',
            text: 'Ask the authority to provide its road-specific inspection records, defect reports and response records if it intends to rely on Section 58. Assess those records with the timing, defect evidence, causation and loss.',
          },
        ],
      }}
      faqs={[
        {
          question: 'Do I need a solicitor to send a pothole claim letter?',
          answer: 'No. Pothole claims are typically small claims (under £10,000) and are designed to be handled without a solicitor. A clear, well-evidenced letter citing Section 41 of the Highways Act 1980 is all you need to start.',
        },
        {
          question: 'Where do I send the letter?',
          answer: 'To the claims or insurance department of the highway authority responsible for the road — usually your local council. Many councils accept claims by email or through an online form. Check your council\u2019s website, or find the right authority on our councils directory.',
        },
        {
          question: 'Should I send it by email or post?',
          answer: 'Either works, but keep proof. If emailing, request a read receipt or acknowledgement. If posting, use recorded delivery. The date you can prove the council received your claim matters if you later escalate.',
        },
        {
          question: 'How much compensation can I claim?',
          answer: 'The reasonable cost of repairing the damage caused — typically a tyre, wheel, tracking or suspension repair, supported by an itemised quote or invoice. Typical pothole claims range from £100 to over £1,000 depending on the damage.',
        },
        {
          question: 'What if the council rejects my claim?',
          answer: 'Most councils reject first claims with a standard Section 58 defence letter. That is not the end — you can challenge the rejection by scrutinising their inspection records and, if needed, escalate to the small claims court. See our guide to understanding rejection reasons.',
        },
        {
          question: 'Can Fixtyer write this letter for me?',
          answer: 'Yes. Fixtyer\u2019s claim pack includes a professionally drafted claim letter tailored to your incident, an organised evidence bundle, an automatic FixMyStreet prior-report check, and a submission guide for your specific council — for a one-time £35 fee.',
        },
      ]}
    >
      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        }
        title="Before You Use This Template"
      >
        <p className="mb-4">
          A pothole claim letter is a formal request for compensation from the highway authority
          that failed to maintain the road. The legal foundation is{' '}
          <Link to="/guides/section-41-explained" className={inlineLink}>
            Section 41 of the Highways Act 1980
          </Link>
          , which places a statutory duty on councils to keep their roads in repair. Before you
          send anything, make sure you actually have{' '}
          <Link to="/guides/what-makes-a-valid-claim" className={inlineLink}>
            a valid claim
          </Link>{' '}
          and that you're writing to the{' '}
          <Link to="/guides/who-is-responsible" className={inlineLink}>
            right authority
          </Link>{' '}
          — local roads belong to your council, while motorways and major A-roads belong to
          National Highways.
        </p>
        <GuideTip variant="warning" title="Evidence first, letter second">
          The letter is the easy part. Claims succeed or fail on evidence — photos of the pothole
          with measurements, photos of the damage, and an itemised repair quote. Gather these
          before you write.
        </GuideTip>
      </GuideSection>

      <LetterTemplateBlock />

      <GuideSteps>
        <GuideStep number={1} title="Identify the Right Highway Authority">
          <p className="mb-2">
            Find out who maintains the road where you hit the pothole. For most local roads this
            is your council — you can{' '}
            <Link to="/councils" className={inlineLink}>
              look up your council's claims process and road record here
            </Link>
            .
          </p>
        </GuideStep>

        <GuideStep number={2} title="State the Facts Precisely">
          <p className="mb-2">
            Vague claims get rejected. Include the date and time, the road name and town, and a
            description of the pothole's location that someone could find from your words alone.
            Include its approximate width and depth — depth matters most, as many councils use a
            40mm intervention threshold.
          </p>
        </GuideStep>

        <GuideStep number={3} title="Cite Section 41">
          <p className="mb-2">
            Referencing the statutory duty signals that you know your rights and won't be fobbed
            off with a form-letter rejection. You don't need legal jargon beyond this — one
            sentence is enough.
          </p>
        </GuideStep>

        <GuideStep number={4} title="Attach Your Evidence">
          <ul className="text-sm text-surface-600 space-y-1 mb-2">
            <li>
              •{' '}
              <Link to="/guides/photographing-pothole-damage" className={inlineLink}>
                Photos of the pothole
              </Link>{' '}
              showing size, depth and context
            </li>
            <li>
              •{' '}
              <Link to="/guides/documenting-vehicle-damage" className={inlineLink}>
                Photos of the vehicle damage
              </Link>
            </li>
            <li>
              • An{' '}
              <Link to="/guides/getting-repair-quotes" className={inlineLink}>
                itemised repair quote or invoice
              </Link>
            </li>
            <li>
              • Any{' '}
              <Link to="/guides/prior-reports-explained" className={inlineLink}>
                prior reports of the pothole
              </Link>{' '}
              (check FixMyStreet)
            </li>
          </ul>
        </GuideStep>

        <GuideStep number={5} title="Request Their Section 58 Records">
          <p className="mb-2">
            The council's main escape route is the{' '}
            <Link to="/guides/section-58-defence" className={inlineLink}>
              Section 58 defence
            </Link>{' '}
            — claiming they had a reasonable inspection system. Asking for their inspection
            records lets you compare the policy with the road-specific evidence. Missed inspections
            or verified, ignored defect reports may be relevant, but their effect depends on timing,
            the inspection regime, the authority&apos;s response, causation and loss.
          </p>
        </GuideStep>
      </GuideSteps>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        }
        title="Mistakes That Get Claims Rejected"
      >
        <GuideGrid cols={2}>
          <GuideCard title="No measurements">
            "A large pothole" is easy to dismiss. "Approximately 60cm wide and 8cm deep" is
            evidence. Photograph it with a ruler, tape measure or everyday object for scale.
          </GuideCard>
          <GuideCard title="Vague location">
            If the council's inspector can't find the pothole from your description, they'll
            dispute it existed. Use distances from junctions, lamp posts or house numbers — or
            GPS coordinates.
          </GuideCard>
          <GuideCard title="No repair quote">
            Claiming a round number without an itemised quote or invoice invites rejection.
            Get the garage to itemise parts and labour, and to note the damage is consistent
            with a pothole impact.
          </GuideCard>
          <GuideCard title="Accepting the first rejection">
            Most councils reject first claims with a boilerplate Section 58 letter, expecting
            you to give up. Scrutinise their inspection records and challenge weak defences.
          </GuideCard>
        </GuideGrid>
      </GuideSection>

      <GuideSection
        icon={
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
        }
        title="Prefer It Done for You?"
      >
        <p className="mb-4">
          This template works — but a template can't check FixMyStreet for prior reports of your
          pothole, organise your evidence into a bundle, or tailor the letter to your specific
          circumstances. Fixtyer can organise the information you provide, surface nearby
          third-party report leads for verification and prepare draft documents for your review
          for a one-time £35 fee, with{' '}
          <Link to="/pricing" className={inlineLink}>
            no percentage taken from your compensation
          </Link>
          . See{' '}
          <Link to="/how-it-works" className={inlineLink}>
            how it works
          </Link>
          .
        </p>
        <GuideTip variant="info" title="Treat prior reports as research leads">
          A nearby FixMyStreet entry does not prove it concerns the same defect or that the
          authority received it. Verify the location, date, responsible authority, status and
          receipt against the authority&apos;s own records before relying on it. Its relevance
          depends on the wider defect, inspection, response, causation and loss evidence.
        </GuideTip>
      </GuideSection>
    </GuideLayout>
  );
}
